import React, { forwardRef, useCallback, useMemo } from 'react';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import EditionsIcon from '@atlaskit/icon-lab/core/editions';
import CreditCardIcon from '@atlaskit/icon/core/credit-card';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import type { NewIconProps } from '@atlaskit/icon/types';
import {
	TopNavButton,
	TopNavLinkButton,
} from '@atlaskit/navigation-system/experimental/top-nav-button';
import { useHasCustomTheme } from '@atlaskit/navigation-system/experimental/use-has-custom-theme';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	Anchor,
	type AnchorProps,
	Box,
	Pressable,
	type PressableProps,
	xcss,
	// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
} from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { type TouchpointIntent } from '@atlassian/touchpoint-commons-event-analytics';

import { type LinkId } from '../analytics/types';
import { UiViewedEvent } from '../analytics/ui-viewed-event';
import { useAnalytics } from '../analytics/use-analytics';
import { useEditionAwareness } from '../services/edition-awareness-context';
import { useGetExperimentValue } from '../services/experiment-context';
import { isEligibleForEAtoOSBExperiment } from '../services/is-eligible-for-ea-to-osb-experiment';
import { type ButtonContent, useButtonContent } from '../services/use-button-content';
import { useTracedUrl } from '../services/use-traced-url';

export const EDITION_AWARENESS_SPOTLIGHT_TARGET = 'edition-awareness';

const buttonStyles = xcss({
	borderWidth: 'border.width',
	font: 'font.body',
	fontWeight: 'font.weight.medium',
	borderStyle: 'solid',
	backgroundColor: 'elevation.surface',
	borderRadius: 'radius.small',
	paddingInline: 'space.150',
	paddingBlock: 'space.050',
	minHeight: '32px',
	color: 'color.text',
	transitionProperty: 'color, background-color',
	transitionDuration: '200ms',
	display: 'flex',
	gap: 'space.100',
	alignItems: 'center',
	textDecoration: 'none',

	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
		textDecoration: 'none',
		color: 'color.text',
	},

	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
		textDecoration: 'none',
	},
});

const defaultStyles = xcss({
	color: 'color.text.discovery',
	borderColor: 'color.border.discovery',

	':hover': {
		backgroundColor: 'color.background.accent.purple.subtlest.hovered',
		color: 'color.text.discovery',
	},

	':active': {
		backgroundColor: 'color.background.accent.purple.subtlest.pressed',
		color: 'color.text.discovery',
	},
});

const warningStyles = xcss({
	borderColor: 'color.border.warning',
});

const dangerStyles = xcss({
	borderColor: 'color.border.danger',
});

const dismissibleStyles = xcss({
	color: 'color.text.discovery',
	borderColor: 'color.border.discovery',
	borderTopRightRadius: '0',
	borderBottomRightRadius: '0',

	':hover': {
		color: 'color.text.discovery',
		backgroundColor: 'color.background.accent.purple.subtlest.hovered',
	},

	':active': {
		backgroundColor: 'color.background.accent.purple.subtlest.pressed',
		textDecoration: 'none',
	},
});

const iconStyles = xcss({
	display: 'flex',
	// icon size cannot grow and shrink
	flexGrow: 0,
	flexShrink: 0,
	alignSelf: 'center',
	userSelect: 'none',
});

function Icon({
	appearance,
	icon,
	color,
}: {
	appearance?: ButtonContent['appearance'];
	/** Used in the top nav when a customer has a custom theme applied */
	color?: NewIconProps['color'];
	icon: ButtonContent['icon'];
}) {
	if (icon === 'warning') {
		const defaultColor =
			appearance === 'danger' ? token('color.icon.danger') : token('color.icon.warning');

		return <StatusWarningIcon label="" color={color ?? defaultColor} size="small" />;
	}

	if (icon === 'credit-card') {
		return (
			<CreditCardIcon label="" color={color ?? token('color.icon.discovery')} LEGACY_size="small" />
		);
	}

	return <EditionsIcon label="" color={color ?? token('color.icon.discovery')} />;
}

export const EditionAwarenessButton: React.ForwardRefExoticComponent<
	Omit<Omit<PressableProps, 'children'>, 'ref'> & React.RefAttributes<HTMLButtonElement>
> = forwardRef<HTMLButtonElement, Omit<PressableProps, 'children'>>(function (
	{ onClick, ...rest },
	ref,
) {
	const buttonContent = useButtonContent();

	if (buttonContent === null) {
		throw new Error('The button content is null, please check the context');
	}

	const { text, icon, appearance = 'default', isDismissible, isEducationDiscount } = buttonContent;

	const hasCustomTheme = useHasCustomTheme();

	const { sendAnalyticsEvent } = useAnalytics();
	const context = useEditionAwareness();
	const { getExperimentValue } = useGetExperimentValue();

	const analyticsAttributes = useMemo(
		() => ({
			type: 'button',
			icon,
			appearance,
			isEducationDiscount,
		}),
		[icon, appearance, isEducationDiscount],
	);

	const handleOnClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			onClick?.(e, analyticsEvent);

			// Fire exposure for ea_to_osb_experiment control tenants
			if (isEligibleForEAtoOSBExperiment(context)) {
				getExperimentValue({
					statsigParams: {
						experimentName: 'ea_to_osb_experiment',
						parameterName: 'cohort',
						defaultValue: 'not-enrolled',
					},
					trackEventParameters: {
						trackEventGrowthInitiative: 'tako',
					},
				});
			}

			const updatedAnalyticsEvent = analyticsEvent.update((payload) => ({
				...payload,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'editionAwareness',
				attributes: analyticsAttributes,
			}));
			sendAnalyticsEvent(updatedAnalyticsEvent);
		},
		[onClick, sendAnalyticsEvent, analyticsAttributes, getExperimentValue, context],
	);

	const handleOnMouseEnter = useCallback(() => {
		if (!fg('ea_hover_analytics_event_gate')) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'hovered',
			actionSubject: 'button',
			actionSubjectId: 'editionAwareness',
			attributes: analyticsAttributes,
		});
	}, [sendAnalyticsEvent, analyticsAttributes]);

	if (hasCustomTheme) {
		// When there's a custom theme applied we just return a button which gets styled by the theme
		return (
			<SpotlightTarget name={EDITION_AWARENESS_SPOTLIGHT_TARGET}>
				<TopNavButton
					{...rest}
					ref={ref}
					onClick={handleOnClick}
					onMouseEnter={handleOnMouseEnter}
					iconBefore={() => <Icon appearance={appearance} icon={icon} color="currentColor" />}
				>
					{text}
					<UiViewedEvent
						actionSubjectId="editionAwareness"
						actionSubject="button"
						attributes={analyticsAttributes}
					/>
				</TopNavButton>
			</SpotlightTarget>
		);
	}

	return (
		<SpotlightTarget name={EDITION_AWARENESS_SPOTLIGHT_TARGET}>
			<Pressable
				{...rest}
				ref={ref}
				xcss={[
					buttonStyles,
					appearance === 'default' && defaultStyles,
					appearance === 'warning' && warningStyles,
					appearance === 'danger' && dangerStyles,
					isDismissible && dismissibleStyles,
				]}
				onClick={handleOnClick}
				onMouseEnter={handleOnMouseEnter}
			>
				<Box xcss={iconStyles}>
					<Icon appearance={appearance} icon={icon} />
				</Box>
				{text}
				<UiViewedEvent
					actionSubjectId="editionAwareness"
					actionSubject="button"
					attributes={analyticsAttributes}
				/>
			</Pressable>
		</SpotlightTarget>
	);
});

export const EditionAwarenessLink: React.ForwardRefExoticComponent<
	Omit<
		Omit<AnchorProps, 'children'> & {
			linkId: LinkId;
			touchpointIntent: TouchpointIntent;
		},
		'ref'
	> &
		React.RefAttributes<HTMLAnchorElement>
> = forwardRef<
	HTMLAnchorElement,
	Omit<AnchorProps, 'children'> & {
		linkId: LinkId;
		touchpointIntent: TouchpointIntent;
	}
>(function ({ onClick, href, touchpointIntent, linkId, ...rest }, ref) {
	const buttonContent = useButtonContent();
	if (buttonContent === null) {
		throw new Error('The button content is null, please check the context');
	}

	const { text, icon, appearance = 'default' } = buttonContent;

	const hasCustomTheme = useHasCustomTheme();

	const { sendAnalyticsEvent } = useAnalytics();
	const { origin, url: tracedUrl } = useTracedUrl(href);
	if (!tracedUrl) {
		throw new Error('The link url is undefined, please check the context');
	}
	const analyticsAttributes = useMemo(
		() => ({
			type: 'link',
			icon,
			appearance,
			touchpointIntent,
			linkId,
			...origin.toAnalyticsAttributes(),
		}),
		[icon, appearance, touchpointIntent, linkId, origin],
	);

	const handleOnClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, analyticsEvent: UIAnalyticsEvent) => {
			onClick?.(e, analyticsEvent);
			const updatedAnalyticsEvent = analyticsEvent.update((payload) => ({
				...payload,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'editionAwareness',
				attributes: analyticsAttributes,
			}));
			sendAnalyticsEvent(updatedAnalyticsEvent);
		},
		[onClick, sendAnalyticsEvent, analyticsAttributes],
	);

	const handleOnMouseEnter = useCallback(() => {
		if (!fg('ea_hover_analytics_event_gate')) {
			return;
		}

		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'hovered',
			actionSubject: 'button',
			actionSubjectId: 'editionAwareness',
			attributes: analyticsAttributes,
		});
	}, [sendAnalyticsEvent, analyticsAttributes]);

	if (hasCustomTheme) {
		// When there's a custom theme applied we just return a button which gets styled by the theme
		return (
			<SpotlightTarget name={EDITION_AWARENESS_SPOTLIGHT_TARGET}>
				<TopNavLinkButton
					{...rest}
					href={tracedUrl}
					ref={ref}
					onClick={handleOnClick}
					onMouseEnter={handleOnMouseEnter}
					iconBefore={() => <Icon appearance={appearance} icon={icon} color="currentColor" />}
				>
					{text}
					<UiViewedEvent
						actionSubjectId="editionAwareness"
						actionSubject="button"
						attributes={analyticsAttributes}
					/>
				</TopNavLinkButton>
			</SpotlightTarget>
		);
	}

	return (
		<SpotlightTarget name={EDITION_AWARENESS_SPOTLIGHT_TARGET}>
			<Anchor
				{...rest}
				href={tracedUrl}
				ref={ref}
				xcss={[
					buttonStyles,
					appearance === 'default' && defaultStyles,
					appearance === 'warning' && warningStyles,
					appearance === 'danger' && dangerStyles,
				]}
				onClick={handleOnClick}
				onMouseEnter={handleOnMouseEnter}
			>
				<Box xcss={iconStyles}>
					<Icon appearance={appearance} icon={icon} />
				</Box>
				{text}
				<UiViewedEvent
					actionSubjectId="editionAwareness"
					actionSubject="button"
					attributes={analyticsAttributes}
				/>
			</Anchor>
		</SpotlightTarget>
	);
});
